import { useEffect, useRef, useState } from 'react';
import useOpeningHours from '~Hooks/useOpeningHours';
import { useLogger } from '~Hooks';

const MINUTE_MS = 60_000;

/**
 * Whether the venue is open right now, from its opening hours. `null` until
 * the hours have loaded.
 *
 * Cheaper than the kiosk's version: it re-checks on each minute boundary
 * rather than a drifting interval, and sets state only when the answer flips,
 * so a caller re-renders twice a day - at opening and closing - not every minute.
 */
const useVenueOpen = (): boolean | null => {
  const logger = useLogger('epos-venue-open');
  const openingHours = useOpeningHours();
  const [isOpen, setIsOpen] = useState<boolean | null>(null);

  /** The check reads the latest hours through a ref, so the timer never restarts on a render. */
  const checkRef = useRef(openingHours.checkOpeningHoursForDate);
  checkRef.current = openingHours.checkOpeningHoursForDate;

  /** Hours arrived or changed - re-evaluate straight away instead of waiting for the tick. */
  const hoursLoaded = !openingHours.loading;
  const todayIsOpen = openingHours.today.isOpen;

  useEffect(() => {
    if (!hoursLoaded) {
      return undefined;
    }

    const evaluate = () => {
      const next = checkRef.current(new Date()).isOpen;
      // Same value -> React bails out, nothing re-renders.
      setIsOpen((current) => {
        if (current !== next) {
          logger.info('Venue availability changed', { isOpen: next });
        }
        return next;
      });
    };

    evaluate();

    let timer: number | undefined;
    const scheduleNextMinute = () => {
      timer = window.setTimeout(() => {
        evaluate();
        scheduleNextMinute();
      }, MINUTE_MS - (Date.now() % MINUTE_MS));
    };
    scheduleNextMinute();

    return () => window.clearTimeout(timer);
  }, [hoursLoaded, todayIsOpen]);

  return isOpen;
};

export default useVenueOpen;
